import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';

import { useForwardCatalog, useSpotCatalog } from '@/features/catalog';
import { AppFormProvider } from '@/features/dashboard/components/AppForm/AppForm/context';
import { DashboardDrawer } from '@/features/dashboard/components/Drawer';
import {
  DrawerStepsProvider,
  useDrawerSteps
} from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import DrawerOpenRequestHandler from '@/features/dashboard/components/DrawerOpenRequestHandler';
import { DashboardStep } from '@/features/dashboard/constants/DashboardStep';
import { useDashboardRouting } from '@/features/dashboard/hooks/routing/useDashboardRouting';
import { useDashboardStepControl } from '@/features/dashboard/hooks/routing/useDashboardStepControl';
import { EmptyDashboard } from '@/features/dashboard/pages/Empty';
import { ViewDashboard } from '@/features/dashboard/pages/View';
import { useDashboardEditLockContext } from '@/features/dashboard/state/DashboardEditLockContext';
import { MarketsSkeleton } from '@/features/markets';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';

import { EditDashboard } from './pages/Edit/EditDashboardPage';

const EditLockDrawerSync = ({
  setStep,
  step
}: {
  setStep: (step: DashboardStep) => void;
  step: DashboardStep;
}) => {
  const { setForceReleaseCallback, releaseEditLock } = useDashboardEditLockContext();
  const { closeDrawer, drawerStep } = useDrawerSteps();
  const prevDrawerStepRef = useRef(drawerStep);

  const forceReleaseEdit = useEffectEvent(() => {
    setStep(DashboardStep.View);
    closeDrawer();
  });

  useEffect(() => {
    setForceReleaseCallback(forceReleaseEdit);
    return () => {
      setForceReleaseCallback(() => {});
    };
  }, [setForceReleaseCallback]);

  useEffect(() => {
    const prev = prevDrawerStepRef.current;
    prevDrawerStepRef.current = drawerStep;
    if (prev !== null && drawerStep === null && step !== DashboardStep.Edit) {
      releaseEditLock();
    }
  }, [drawerStep, step, releaseEditLock]);

  return null;
};

const Dashboard = () => {
  const [isOptimisticSavePending, setIsOptimisticSavePending] = useState(false);
  const {
    step,
    setStep,
    guardedSetStep,
    dashboardConfig,
    dashboardStructure,
    activeTab,
    tabNames,
    handleTabChange,
    routeDashboardName,
    isLoading,
    isNewDashboard
  } = useDashboardRouting();

  useDashboardStepControl(step);

  useForwardCatalog();
  useSpotCatalog();

  const handleStepChange = useCallback(
    (nextStep: DashboardStep) => {
      if (nextStep !== DashboardStep.Edit) {
        setIsOptimisticSavePending(false);
      }
      setStep(nextStep);
    },
    [setStep]
  );

  const viewContent = useMemo(() => {
    if (!dashboardConfig || dashboardConfig.length === 0) {
      return (
        <EmptyDashboard
          activeTab={activeTab}
          isNewDashboard={isNewDashboard}
          setStep={guardedSetStep}
          tabNames={tabNames}
          title={routeDashboardName}
          onTabChange={handleTabChange}
        />
      );
    }

    return (
      <ViewDashboard
        activeTab={activeTab}
        dashboardConfig={dashboardConfig}
        setStep={guardedSetStep}
        tabNames={tabNames}
        title={routeDashboardName}
        onTabChange={handleTabChange}
      />
    );
  }, [
    activeTab,
    tabNames,
    handleTabChange,
    dashboardConfig,
    routeDashboardName,
    guardedSetStep,
    isNewDashboard
  ]);

  const content = useMemo(() => {
    if (isLoading) return <MarketsSkeleton />;
    if (step !== DashboardStep.Edit) return viewContent;

    return (
      <>
        <div hidden={isOptimisticSavePending}>
          <EditDashboard
            activeTab={activeTab}
            dashboardConfig={dashboardConfig ?? []}
            dashboardName={routeDashboardName}
            dashboardStructure={dashboardStructure}
            initialDashboardName={routeDashboardName}
            onClose={() => handleStepChange(DashboardStep.View)}
            onOptimisticSaveError={() => setIsOptimisticSavePending(false)}
            onOptimisticSaveStart={() => setIsOptimisticSavePending(true)}
          />
        </div>
        {isOptimisticSavePending ? viewContent : null}
      </>
    );
  }, [
    isLoading,
    step,
    viewContent,
    isOptimisticSavePending,
    activeTab,
    dashboardConfig,
    routeDashboardName,
    dashboardStructure,
    handleStepChange
  ]);

  return (
    <PageInformationContext
      value={{
        mainPageTitle: routeDashboardName
      }}
    >
      <AppFormProvider>
        <DrawerStepsProvider>
          <DrawerOpenRequestHandler />
          <EditLockDrawerSync
            setStep={handleStepChange}
            step={step}
          />
          {content}
          <DashboardDrawer />
        </DrawerStepsProvider>
      </AppFormProvider>
    </PageInformationContext>
  );
};

export default Dashboard;
